import useTranslation from 'hooks/useTranslation'
import { LOCATION_STATES } from 'utils/constants'
import TimePickerPopup from './TimePickerPopup'
import DatePickerPopup from './DatePickerPopup'
import usePopupState from 'hooks/usePopupState'
import React, { useRef, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { v7 } from 'uuid'

interface BaseProps {
  disabled?: boolean
  isPopup?: boolean
  warning?: string
  value?: string
  name?: string
  label?: string
  required?: boolean
  placeholder?: string
  setValue: (value: string) => void
}

interface DatePickerProps extends BaseProps {
  minDate?: string
  maxDate?: string
}

interface BasePickerProps extends BaseProps {
  type: 'date' | 'time'
  showPopup: React.Dispatch<React.SetStateAction<boolean>>
}

const BasePicker: React.FC<BasePickerProps> = ({ disabled, warning, name, isPopup, required, value, type, label, placeholder, showPopup }) => {
  const { push } = useHistory()
  return (
    <div className={`input__default${disabled ? ' --disabled' : warning ? ' --warning' : ''}`}>
      <label className='input__label' htmlFor={name}>
        <span className='label--warning'>{required ? '* ' : ''}</span>
        <span>{label} </span>
        {warning && <span className='label--warning'>
          - {warning}
        </span>}
      </label>

      <div
        className='input__main'
        onClick={() => {
          if (!disabled) {
            if (isPopup) {
              showPopup(true)
            } else {
              push(window.location.pathname, LOCATION_STATES.popup({ type: name }))
            }
          }
        }}>
        <input
          disabled
          id={name}
          name={name}
          value={value || ''}
          className='input --right'
          placeholder={placeholder}
          type={value ? type : 'text'}
          style={{ pointerEvents: 'none' }}
        />

        <div className='input__icon --right'>
          <i className={`icon icon--${type === 'time' ? type : 'calendar'}`} />
        </div>
      </div>
    </div>
  )
}

const DatePicker: React.FC<DatePickerProps> = ({ minDate, maxDate, ...props }) => {
  const name = useRef(`datePicker-${props.name || v7()}`)
  const [isPopup, showPopup] = useState(false)
  const { popup } = usePopupState()

  const { t } = useTranslation('form')

  return (
    <>
      {(isPopup || (popup as any)?.type === name.current) && <DatePickerPopup
        value={props.value}
        minDate={minDate}
        maxDate={maxDate}
        onChange={props.setValue}
        close={isPopup
          ? () => { showPopup(false) }
          : undefined
        }
      />}
      <BasePicker
        placeholder={t('selectDate')}
        label={t('date')}
        {...props}
        type='date'
        name={name.current}
        showPopup={showPopup}
      />
    </>
  )
}

const TimePicker: React.FC<DatePickerProps> = (props) => {
  const name = useRef(`timePicker-${props.name || v7()}`)
  const [isPopup, showPopup] = useState(false)
  const { popup } = usePopupState()

  const { t } = useTranslation('form')

  return (
    <>
      {(isPopup || (popup as any)?.type === name.current) && <TimePickerPopup
        time={props.value}
        onChange={props.setValue}
        close={isPopup
          ? () => { showPopup(false) }
          : undefined
        }
      />}
      <BasePicker
        placeholder={t('selectTime')}
        label={t('time')}
        {...props}
        type='time'
        name={name.current}
        showPopup={showPopup}
      />
    </>
  )
}

export {
  DatePicker,
  TimePicker
}